import { useEffect, useMemo, useRef } from "react";

import { Upload } from "lucide-react";

import { FILE_EXTENSIONS, FILE_SIZES } from "@/constants/file";
import { useBundleSplitterTasks } from "@/hooks/queries/use-bundle-splitter";
import { useAuth } from "@/hooks/useAuth";

import { FileInput } from "@components/FileInput";
import { useToast } from "@components/PersistentToast/hooks/useToast";
import { Button } from "@components/ui/button";

import {
  getDismissedBundleTaskIds,
  pruneDismissedBundleTaskIds,
} from "@utils/dismissedBundleTasks";

import { FileList, RehydratedTask } from "./BundleUpload/FileList";
import { extractUserId } from "./BundleUpload/utils/extractUserId";

type UploadBundleFileProps = {
  caseNumber?: string;
  onToastOpen?: () => void;
  onToastClose?: () => void;
};

const REHYDRATABLE_STATUSES = new Set(["PENDING", "PROCESSING"]);

export const UploadBundleFile = ({
  caseNumber = "",
  onToastOpen,
  onToastClose,
}: UploadBundleFileProps) => {
  const { persistentToast, dismissToast } = useToast();
  const { user } = useAuth();

  // STP-77: on mount, fetch in-flight bundle-splitter tasks for this case so
  // that a refresh / re-login can resume an existing job instead of orphaning it.
  const { data: tasksData } = useBundleSplitterTasks({
    case_number: caseNumber || undefined,
    enabled: !!caseNumber,
  });

  const rehydratedTasks = useMemo<RehydratedTask[]>(() => {
    if (!tasksData?.tasks) return [];
    // Drop any locally-dismissed task_ids that the server no longer reports,
    // then exclude the ones still dismissed from the rehydration list.
    pruneDismissedBundleTaskIds(tasksData.tasks.map((t) => t.task_id));
    const dismissed = getDismissedBundleTaskIds();
    return tasksData.tasks
      .filter((t) => REHYDRATABLE_STATUSES.has(t.status))
      .filter((t) => !dismissed.has(t.task_id))
      .flatMap((t) => {
        // STP-86: stale tasks can come back with no file metadata; rehydrating
        // them surfaces a blank row and looks like an unprompted modal.
        const fileName = t.file_names?.[0]?.trim();
        if (!fileName) return [];
        return [
          {
            taskId: t.task_id,
            fileName,
            fileSize: t.file_size ?? undefined,
            phase: "polling" as const,
          },
        ];
      });
  }, [tasksData]);

  const handleFileSelection = (files: File[]) => {
    if (files.length === 0) return;

    // Close the upload dialog toast
    dismissToast();

    // Show the FileUploadConfirmation component in a new toast
    persistentToast({
      title: `Bundle File${files.length > 1 ? "s" : ""}`,
      content: (
        <FileList
          files={files}
          caseNumber={caseNumber}
          userId={extractUserId(user)}
          onToastClose={onToastClose}
          onAllCleared={handleOpenUploadDialog}
        />
      ),
    });
  };

  const handleOpenUploadDialog = () => {
    // Open persistent toast with FileInput
    persistentToast({
      title: "Uploading Bundle File",
      content: (
        <FileInput
          accept={[...FILE_EXTENSIONS.DOCUMENTS]}
          multiple
          allowedTypes={[...FILE_EXTENSIONS.DOCUMENTS]}
          size="sm"
          maxSize={FILE_SIZES.MB_100}
          placeholder="Drag and drop your file here"
          onFilesSelected={handleFileSelection}
          showFileList={false}
        />
      ),
    });

    // Notify parent that toast is open
    onToastOpen?.();
  };

  const hasRehydratedRef = useRef(false);

  useEffect(() => {
    if (hasRehydratedRef.current) return;
    if (!caseNumber) return;
    if (rehydratedTasks.length === 0) return;

    hasRehydratedRef.current = true;
    persistentToast({
      title: `Bundle File${rehydratedTasks.length > 1 ? "s" : ""}`,
      content: (
        <FileList
          rehydratedTasks={rehydratedTasks}
          caseNumber={caseNumber}
          userId={extractUserId(user)}
          onToastClose={onToastClose}
          onAllCleared={handleOpenUploadDialog}
        />
      ),
    });
    onToastOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber, rehydratedTasks]);

  return (
    <Button size="sm" onClick={handleOpenUploadDialog}>
      <Upload size={16} />
      Upload case bundle
    </Button>
  );
};
